/**
 * 2.0 wave 2, W2-CHROME (spec 7.5, CH-6) — "Sign out" asks first.
 *
 * One hook, three doors: the avatar menu and the native avatar sheet (TopBar),
 * the desktop side nav's sign-out icon (SideNav), and the Account section's
 * button (W2-SET-SCREENS). Each calls `requestSignOut()` and renders `dialog`.
 *
 * The question is Appendix C 50 to 52, word for word: title "Sign out of Tayro?",
 * line "You can sign back in with Google or Apple.", buttons "Stay signed in"
 * (default focus; Escape and a tap on the backdrop also stay) and "Sign out".
 *
 * "Sign out" runs exactly the sign-out the doors ran before, then the same
 * navigation: the native shell lands on `/welcome`, the web on `/landing`.
 * A sign-out that throws still clears local state, as before.
 *
 * The dialog is PORTALED to `document.body`: the top bar is a transformed
 * ancestor, which would otherwise become the containing block of this fixed
 * overlay and pin it inside the 57px bar. It reuses the shared team
 * `ConfirmDialog` (focus trap, Escape, backdrop) with the optional
 * `cancelLabel` prop; the sheet variant rests above the tab bar on a phone and
 * the modal variant centers on a wide screen. It asks with `tone="neutral"`
 * (Claude Design item 8): signing out is not a destructive act, so no red top
 * rule and a marine confirm; remove and leave keep the default danger tone.
 */

import { useCallback, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { isNativeApp } from '../../../lib/platform';
import { ConfirmDialog } from '../../team/ConfirmDialog';

export interface SignOutWithConfirm {
  /** Open the question. Nothing is signed out until the user confirms. */
  requestSignOut: () => void;
  /** Render this once, anywhere under the router. Null while the question is closed. */
  dialog: ReactNode;
}

/**
 * The question itself. A component of its own so the viewport read (sheet on a
 * phone, modal when wide) happens only while it is open: the hook below, which
 * the top bar and the side nav call on every render, stays free of effects.
 */
function SignOutQuestion({ onStay, onConfirm }: { onStay: () => void; onConfirm: () => void }) {
  const wide = useMediaQuery('(min-width: 1024px)');
  return (
    <ConfirmDialog
      variant={wide ? 'modal' : 'sheet'}
      tone="neutral"
      title="Sign out of Tayro?"
      body="You can sign back in with Google or Apple."
      confirmLabel="Sign out"
      cancelLabel="Stay signed in"
      onClose={onStay}
      onConfirm={onConfirm}
    />
  );
}

export function useSignOutWithConfirm(): SignOutWithConfirm {
  const { signOut } = useAuthContext();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // One sign-out per confirmation, however fast the taps come.
  const running = useRef(false);

  const requestSignOut = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);

  const confirm = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    setOpen(false);
    try {
      await signOut();
    } catch {
      /* clears local state regardless */
    }
    running.current = false;
    // D10: in the shell every signed-out path lands on the native welcome;
    // the web keeps its marketing landing.
    navigate(isNativeApp() ? '/welcome' : '/landing');
  }, [signOut, navigate]);

  const dialog: ReactNode =
    open && typeof document !== 'undefined'
      ? createPortal(
          <SignOutQuestion onStay={close} onConfirm={() => void confirm()} />,
          document.body,
        )
      : null;

  return { requestSignOut, dialog };
}
